import { SNOWMEN, snowmanSpec } from "./config";

// Ladder progression: which snowman the player faces next. Persisted as a
// tiny JSON blob so it survives reloads.
export const PROGRESSION_KEY = "snowball-ladder";

export interface Progression {
  level: number; // snowman to face next, 1..SNOWMEN.length
  cleared: number; // highest snowman beaten so far (0 = none)
}

export interface ProgressionStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const INITIAL_PROGRESSION: Progression = { level: 1, cleared: 0 };

function clampLevel(value: unknown, min: number): number {
  const n = typeof value === "number" && Number.isFinite(value) ? value : min;
  return Math.min(SNOWMEN.length, Math.max(min, Math.floor(n)));
}

export function parseProgression(raw: string | null): Progression {
  if (!raw) return { ...INITIAL_PROGRESSION };
  try {
    const saved = JSON.parse(raw) as Partial<Progression> | null;
    const level = clampLevel(saved?.level, 1);
    const cleared = clampLevel(saved?.cleared, 0);
    return { level, cleared };
  } catch {
    return { ...INITIAL_PROGRESSION };
  }
}

export function loadProgression(
  storage: ProgressionStorage | undefined,
): Progression {
  try {
    return parseProgression(storage?.getItem(PROGRESSION_KEY) ?? null);
  } catch {
    return { ...INITIAL_PROGRESSION };
  }
}

export function saveProgression(
  storage: ProgressionStorage | undefined,
  progression: Progression,
): void {
  try {
    storage?.setItem(PROGRESSION_KEY, JSON.stringify(progression));
  } catch {
    // Private mode / quota: progression just won't persist.
  }
}

/**
 * Advance after a match. A win moves up one snowman (the last one stays);
 * a loss, draw or forfeit repeats the same snowman.
 */
export function advanceProgression(
  progression: Progression,
  outcome: "win" | "lose" | "draw",
  winType?: string,
): Progression {
  if (outcome !== "win" || winType === "forfeit") return { ...progression };
  return {
    level: Math.min(SNOWMEN.length, progression.level + 1),
    cleared: Math.max(progression.cleared, progression.level),
  };
}

/** Names for the win/lose screens: who was just fought and who is next. */
export function resultNames(
  before: Progression,
  after: Progression,
): { opponent: string; next: string } {
  return {
    opponent: snowmanSpec(before.level).name,
    next: snowmanSpec(after.level).name,
  };
}
